import React, { useState } from "react";
import { ShieldCheck, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import basiqMarkWhite from "../../assets/basiq-mark-white.png";
import basiqMark from "../../assets/basiq-mark.png";
import { adminLogin, adminMe, adminLogout, apiError } from "../../lib/adminApi";
import { useAdminTheme } from "../../lib/adminTheme";
import { inputCls, btnPrimary, Field, ThemeToggle } from "./ui";

const REMEMBER_KEY = "basiq_admin_remember_email";
const savedEmail = () => { try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; } };

export default function AdminLogin({ onAuthed }) {
  const { isLight, toggle } = useAdminTheme();
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(() => !!savedEmail());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await adminLogin(email.trim(), password, remember);
      try { if (remember) localStorage.setItem(REMEMBER_KEY, email.trim()); else localStorage.removeItem(REMEMBER_KEY); } catch {}
      try {
        onAuthed(await adminMe());
      } catch {
        await adminLogout().catch(() => {});
        setError("Esta conta não tem acesso ao painel administrativo.");
      }
    } catch (err) {
      setError(apiError(err, "Não foi possível entrar"));
      toast.error("Falha no login");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 grid place-items-center px-5 relative overflow-hidden" data-testid="admin-login-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(closest-side, #38BDF8, transparent)" }} />
      <div className="absolute top-4 right-4"><ThemeToggle isLight={isLight} onToggle={toggle} /></div>
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-3xl bg-[#111827]/80 ring-1 ring-white/[0.08] p-8 backdrop-blur">
        <div className="flex items-center gap-3">
          <img src={isLight ? basiqMark : basiqMarkWhite} alt="" className="h-7 w-auto" />
          <div>
            <div className="text-[16px] font-bold leading-none">Painel Admin</div>
            <div className="text-[11px] text-slate-400 mt-1">Acesso exclusivo do administrador</div>
          </div>
        </div>
        <div className="mt-7 space-y-4">
          <Field label="E-mail"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="username" required data-testid="admin-email" /></Field>
          <Field label="Senha">
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-11`} autoComplete="current-password" required data-testid="admin-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="admin-password-toggle" aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-300 cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="admin-remember" className="h-4 w-4 rounded accent-[#38BDF8]" />
            Lembrar meu acesso neste dispositivo (30 dias)
          </label>
          {error && <div className="text-[12.5px] text-rose-300 bg-rose-500/10 ring-1 ring-rose-400/30 rounded-xl px-3 py-2" data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="admin-login-submit"><Lock size={14} /> {busy ? "Entrando…" : "Entrar"}</button>
        </div>
        <div className="mt-6 flex items-center gap-2 text-[11px] text-slate-500"><ShieldCheck size={12} /> Somente contas com perfil de administrador.</div>
      </form>
    </div>
  );
}
